import React, { useState, useEffect } from 'react';
import { Copy, Check, ExternalLink, Code } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ArbeitCheckerDemo() {
  const [embedCode, setEmbedCode] = useState(null);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchEmbedCode();
  }, []);

  const fetchEmbedCode = async () => {
    try {
      const res = await fetch(`${API_URL}/api/thesis-checker/embed-code`);
      const data = await res.json();
      setEmbedCode(data);
    } catch (err) {
      console.error('Error:', err);
    }
    setLoading(false);
  };

  const copyToClipboard = async () => {
    if (!embedCode?.html) return;
    await navigator.clipboard.writeText(embedCode.html);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-screen">
        <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Arbeit-Checker für adagmedia.ch
          </h1>
          <p className="text-gray-600">
            Einbettbares Widget zur Formatierungsprüfung von Abschlussarbeiten
          </p>
        </div>

        {/* Embed Code Section */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
              <Code className="w-5 h-5" />
              Einbettungs-Code
            </h2>
            <button
              onClick={copyToClipboard}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                copied 
                  ? 'bg-green-100 text-green-700' 
                  : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
              }`}
            >
              {copied ? (
                <>
                  <Check className="w-4 h-4" />
                  Kopiert!
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4" />
                  Code kopieren
                </>
              )}
            </button>
          </div>
          
          <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-sm">
            <code>{embedCode?.html || 'Lade...'}</code>
          </pre>
        </div>

        {/* Instructions */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            Integration auf adagmedia.ch
          </h2>
          
          <div className="space-y-4">
            {embedCode?.instructions?.steps?.map((step, idx) => (
              <div key={idx} className="flex gap-3">
                <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-700 font-bold">
                  {idx + 1}
                </div>
                <div className="pt-1 text-gray-700">{step.replace(/^\d+\.\s*/, '')}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Live Preview */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <ExternalLink className="w-5 h-5" />
            Live-Vorschau
          </h2>
          
          <p className="text-gray-600 mb-4">
            So sieht das Widget aus, wenn es auf adagmedia.ch eingebettet wird:
          </p>
          
          {/* Widget Container */}
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 bg-gray-50">
            <div id="arbeit-checker-widget"></div>
          </div>
        </div>

        {/* Load Widget Script */}
        <script
          dangerouslySetInnerHTML={{
            __html: `
              window.ARBEIT_CHECKER_API_URL = '${API_URL}';
            `
          }}
        />
      </div>
    </div>
  );
}
